import {
  CodeXml,
  Download,
  FileText,
  FullscreenIcon,
  RotateCw,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { type ElementType, memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  PDFViewer,
  type PDFViewerRef,
} from "@/components/shared/file-viewers/pdf-viewer";

import { XMLViewer } from "@/components/shared/file-viewers/xml-viewer";
import { SuperAdminOnly } from "@/components/shared/miscellaneous/superadmin-only";
import { ToolbarButton } from "@/components/shared/toolbars/toolbar-button";
import { Badge } from "@/components/ui/badge";
import { Toggle } from "@/components/ui/toggle";
import { TypographySmall } from "@/components/ui/typography";
import { useInvoicePdfSyncSession } from "@/hooks/use-invoice-pdf-sync";
import { isXml } from "@/lib/file-operations/is-xml";
import { openInvoicePdfWindow } from "@/lib/miscellaneous/open-invoice-pdf-window";
import { useTenant } from "@/providers/session";
import { useInvoiceReviewContext } from "./ReviewProvider";

type Props = {
  pdfBlob: string;
  invoiceId: string;
  coordinates: any;
  canvasEnabled: boolean;
  eInvoice?: boolean;
  isFullscreen?: boolean;
  maxWidth?: number;
  ExtraToolBarButtons?: ElementType<{
    invoiceId?: string;
    companySlug?: string;
    isDigitalInvoice?: boolean;
  }>;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  rotation?: number;
  onRotationChange?: (rotation: number) => void;
};

const InvoiceViewerComponent = ({
  pdfBlob,
  invoiceId,
  coordinates,
  isFullscreen,
  maxWidth,
  ExtraToolBarButtons,
  zoom,
  onZoomChange,
  rotation,
  onRotationChange,
}: Props) => {
  const { publishState } = useInvoicePdfSyncSession(invoiceId, isFullscreen);
  const { invoiceReviewState } = useInvoiceReviewContext();
  const { companySlug } = useTenant();
  const { invoiceData } = invoiceReviewState;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [view, setView] = useState<"pdf" | "xml">("pdf");
  const pdfViewerRef = useRef<PDFViewerRef>(null);

  const { t } = useTranslation();

  const isXmlContent = isXml(invoiceData?.invoiceText);

  const handleZoomIn = () => {
    pdfViewerRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    pdfViewerRef.current?.zoomOut();
  };

  const handleRotation = () => {
    pdfViewerRef.current?.rotate();
  };

  const handleDownload = () => {
    pdfViewerRef.current?.download();
  };

  useEffect(() => {
    publishState({
      coordinates,
      pdfBlobData: pdfBlob,
    });
  }, [pdfBlob, coordinates, publishState]);

  useEffect(() => {
    return () => {
      publishState({
        coordinates: undefined,
        pdfBlobData: undefined,
      });
    };
  }, []);

  const setZoom = (value: number) => {
    onZoomChange?.(value);
  };

  const setRotation = (value: number) => {
    onRotationChange?.(value);
  };

  return (
    <div className="h-full flex flex-col bg-gray-200 overflow-hidden pb-1">
      <div className="flex py-2 px-2 justify-end items-center gap-2">
        {totalPages > 0 && (
          <div className="flex items-center bg-white/80 rounded-md px-3 py-1 shadow-sm border border-gray-200">
            <TypographySmall className="font-medium">
              {currentPage}
            </TypographySmall>
            <span className="text-gray-400 mx-1.5">/</span>
            <TypographySmall className="text-gray-500">
              {totalPages}
            </TypographySmall>
          </div>
        )}
        {invoiceData?.isDigitalInvoice && (
          <div>
            <Badge variant="default">{t("e_invoice")}</Badge>
          </div>
        )}
        <div className="flex-1" />
        {isXmlContent && (
          <SuperAdminOnly>
            <Toggle
              pressed={view === "xml"}
              onPressedChange={() => setView("xml")}
            >
              <CodeXml width={20} />
            </Toggle>
          </SuperAdminOnly>
        )}
        <SuperAdminOnly>
          <Toggle
            pressed={view === "pdf"}
            onPressedChange={() => setView("pdf")}
          >
            <FileText width={20} />
          </Toggle>
        </SuperAdminOnly>
        <ToolbarButton
          onClick={handleZoomIn}
          tooltip={t("zoom_in")}
          className="p-1.5"
        >
          <ZoomIn width={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleZoomOut}
          tooltip={t("zoom_out")}
          className="p-1.5"
        >
          <ZoomOut width={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleRotation}
          tooltip={t("rotate")}
          className="p-1.5"
        >
          <RotateCw width={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleDownload}
          tooltip={t("download")}
          className="p-1.5"
        >
          <Download width={18} />
        </ToolbarButton>
        {!isFullscreen && (
          <ToolbarButton
            tooltip={t("invoice_pdf_fullscreen_button")}
            onClick={() => openInvoicePdfWindow(companySlug)}
            className="px-2 py-0 text-xs border border-gray-300 bg-gray-100 font-medium flex items-center h-8 text-gray-800 hover:bg-gray-200 hover:border-gray-400"
          >
            <FullscreenIcon width={14} className="mr-1" />
            {t("invoice_pdf_fullscreen_button")}
          </ToolbarButton>
        )}
        {ExtraToolBarButtons && (
          <ExtraToolBarButtons
            invoiceId={invoiceId}
            companySlug={companySlug}
            isDigitalInvoice={invoiceData?.isDigitalInvoice}
          />
        )}
      </div>
      {view === "pdf" && (
        <div className="flex-1 overflow-auto">
          <PDFViewer
            pdfBlob={pdfBlob}
            ref={pdfViewerRef}
            coordinates={coordinates}
            maxWidth={maxWidth}
            onVisiblePageChange={setCurrentPage}
            onPdfLoad={({ totalPages }) => setTotalPages(totalPages)}
            zoom={zoom}
            onZoomChange={setZoom}
            rotation={rotation}
            onRotationChange={setRotation}
          />
        </div>
      )}
      {view === "xml" && (
        <div className="flex-1 overflow-auto px-4 py-2">
          <XMLViewer xml={invoiceData?.invoiceText ?? ""} />
        </div>
      )}
    </div>
  );
};

export const InvoiceViewer = memo(InvoiceViewerComponent);
